/**
 * Navigation service to handle automatic data loading on navigation changes
 * Prevents manual refresh requirements across the application
 */

import dataLoadingService from './dataLoadingService.js';

class NavigationService {
  constructor() {
    this.currentPath = window.location.pathname;
    this.currentParams = new URLSearchParams(window.location.search);
    this.navigationListeners = new Set();
    this.isInitialized = false;
  }

  /**
   * Initialize the navigation service
   */
  initialize() {
    if (this.isInitialized) {
      console.log('📍 Navigation service already initialized');
      return;
    }

    console.log('📍 Initializing navigation service...');
    
    // Listen for browser navigation events
    window.addEventListener('popstate', this.handlePopState.bind(this));
    
    // Listen for programmatic navigation
    this.interceptPushState();
    this.interceptReplaceState();
    
    // Listen for hash changes
    window.addEventListener('hashchange', this.handleHashChange.bind(this));
    
    // Listen for URL parameter changes
    this.startParameterWatcher();
    
    this.isInitialized = true;
    console.log('✅ Navigation service initialized');
  }

  /**
   * Add a navigation listener
   */
  addNavigationListener(callback) {
    console.log('📍 Adding navigation listener');
    this.navigationListeners.add(callback);
    
    return () => {
      console.log('📍 Removing navigation listener');
      this.navigationListeners.delete(callback);
    };
  }

  /**
   * Notify all navigation listeners
   */
  notifyNavigationListeners(navigationData) {
    console.log('📍 Notifying navigation listeners:', navigationData);
    
    this.navigationListeners.forEach(callback => {
      try {
        callback(navigationData);
      } catch (error) {
        console.error('Error in navigation listener:', error);
      }
    });
  }

  /**
   * Handle browser back/forward navigation
   */
  handlePopState(event) {
    console.log('📍 PopState event detected:', event.state);
    this.handleNavigationChange('popstate', event.state);
  }

  /**
   * Handle hash changes
   */
  handleHashChange(event) {
    console.log('📍 Hash change detected:', { 
      oldURL: event.oldURL, 
      newURL: event.newURL 
    });
    this.handleNavigationChange('hashchange');
  }

  /**
   * Intercept pushState to detect programmatic navigation
   */
  interceptPushState() {
    const originalPushState = window.history.pushState;

    window.history.pushState = (...args) => {
      console.log('📍 PushState intercepted:', args);
      originalPushState.apply(window.history, args);
      this.handleNavigationChange('pushstate', args[0]);
    };
  }

  /**
   * Intercept replaceState to detect programmatic navigation
   */
  interceptReplaceState() {
    const originalReplaceState = window.history.replaceState;

    window.history.replaceState = (...args) => {
      console.log('📍 ReplaceState intercepted:', args);
      originalReplaceState.apply(window.history, args);
      this.handleNavigationChange('replacestate', args[0]);
    };
  }

  /**
   * Start watching for URL parameter changes
   */
  startParameterWatcher() {
    setInterval(() => {
      const currentParams = new URLSearchParams(window.location.search);
      const currentPath = window.location.pathname;
      
      // Check if path or parameters have changed
      if (currentPath !== this.currentPath || 
          currentParams.toString() !== this.currentParams.toString()) {
        
        console.log('📍 URL parameters changed:', {
          oldPath: this.currentPath,
          newPath: currentPath,
          oldParams: this.currentParams.toString(),
          newParams: currentParams.toString()
        });
        
        this.handleNavigationChange('paramchange');
      }
    }, 500); // Check every 500ms
  }

  /**
   * Handle any navigation change
   */
  handleNavigationChange(type, state = null) {
    const newPath = window.location.pathname;
    const newParams = new URLSearchParams(window.location.search);
    
    const navigationData = {
      type,
      oldPath: this.currentPath,
      newPath,
      oldParams: this.currentParams,
      newParams,
      state,
      timestamp: Date.now()
    };

    // Update current state
    this.currentPath = newPath;
    this.currentParams = newParams;

    // Notify listeners
    this.notifyNavigationListeners(navigationData);

    // Trigger data loading service refresh for relevant components
    this.triggerDataRefresh(navigationData);
  }

  /**
   * Trigger data refresh based on navigation
   */
  triggerDataRefresh(navigationData) {
    const { newPath, newParams, oldPath, oldParams } = navigationData;
    
    console.log('📍 Triggering data refresh for navigation change');

    // Check if project ID changed
    const oldProjectId = oldParams.get('id') || oldParams.get('projectId');
    const newProjectId = newParams.get('id') || newParams.get('projectId');
    
    if (oldProjectId !== newProjectId && newProjectId) {
      console.log('📍 Project ID changed, refreshing project-related data');
      dataLoadingService.refreshData('project-management');
      dataLoadingService.refreshData('kanban-board');
    }

    // Check if tab changed
    const oldTab = oldParams.get('tab');
    const newTab = newParams.get('tab');
    
    if (oldTab !== newTab && newTab) {
      console.log('📍 Tab changed, refreshing tab-specific data');
      dataLoadingService.refreshData('project-management');
    }

    // Path-specific refresh logic
    if (newPath !== oldPath) {
      console.log('📍 Path changed, refreshing path-specific data');
      
      if (newPath.includes('/project-management')) {
        dataLoadingService.refreshData('project-management');
      }
      
      if (newPath.includes('/kanban-board')) {
        dataLoadingService.refreshData('kanban-board');
      }
      
      if (newPath.includes('/dashboard') || newPath.includes('/role-based-dashboard')) {
        dataLoadingService.refreshData('dashboard-stats');
        dataLoadingService.refreshData('dashboard-projects');
        dataLoadingService.refreshData('dashboard-notifications');
      }
    }

    // Always refresh if we're on a page that commonly needs fresh data
    if (newPath.includes('/project-management') || 
        newPath.includes('/kanban-board') || 
        newPath.includes('/dashboard')) {
      
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        dataLoadingService.handleNavigationChange(newPath, Object.fromEntries(newParams));
      }, 100);
    }
  }

  /**
   * Manually trigger navigation refresh
   */
  refresh() {
    console.log('📍 Manual navigation refresh triggered');
    this.handleNavigationChange('manual_refresh');
  }

  /**
   * Get current navigation state
   */
  getCurrentState() {
    return {
      path: this.currentPath,
      params: this.currentParams,
      search: this.currentParams.toString()
    };
  }

  /**
   * Clean up navigation service
   */
  cleanup() {
    console.log('📍 Cleaning up navigation service...');
    
    window.removeEventListener('popstate', this.handlePopState.bind(this));
    window.removeEventListener('hashchange', this.handleHashChange.bind(this));
    
    this.navigationListeners.clear();
    this.isInitialized = false;
    
    console.log('✅ Navigation service cleaned up');
  }
}

// Create singleton instance
const navigationService = new NavigationService();

export default navigationService;
